// src/pages/PftMoveAll.js
//
// "Move all" on the PFT Uploads page: every New card whose patient is NOT already in
// the system is added to Lung Center Referrals in one go, after one confirmation that
// says how many still have unchecked items. The match check uses one list loaded for
// the whole batch, and each new patient joins that list as it's created.
//
// Cards that may already be in the system are then checked one at a time (2026-10-19):
// one match -> "Merge with" that record; several (copies of one patient) -> the person
// picks one. Each shows the record's box, current studies, why it matched and a link to
// its patient page. Merge (only differing fields are asked about; Keep is the default),
// "Not this patient: add new", or Skip (the card stays on the page). Nothing is merged
// without that click. Status never changes on a merge.

import { useRef, useState } from "react";
import { GitMerge, Loader2, Users, X, Square } from "lucide-react";
import { findCardMatches, patientHref } from "../services/pftMovePlan";
import {
  createPatientFromCard,
  loadMatchCandidates,
  loadMergePlan,
  MatchFoundError,
  mergeIntoFirestorePatient,
  PatientChangedError,
} from "../services/pftMoveService";
import PftMatchCard from "./PftMatchCard";
import PftMergeRows, { pdfsOfCard, withChoice } from "./PftMergeRows";

const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";
const primary =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/40 to-blue-500/40 border border-purple-300/30 hover:from-purple-500/60 hover:to-blue-500/60 text-sm font-medium text-white transition disabled:opacity-40";

const unchecked = (card) =>
  card.review.filter((r) => !r.resolved && (r.importId === null || card.pendingImports.some((p) => p.id === r.importId))).length;
const matchKey = (m) => `${m.store}:${m.record.id}`;
const NO_CHOICES = { profile: {}, tests: {} };

// scope "ready": the page passes only cards with nothing left to check, and the button
// and heading say so. scope "filtered": the cards matching the page's PFT filters that
// are ready and not left out; `note` says what was skipped. Without a scope (the
// default) everything is exactly as before.
const WORDING = {
  ready: { button: "Move all ready", title: "Move all ready patients to Lung Center Referrals" },
  filtered: { button: "Move filtered", title: "Move filtered patients to Lung Center Referrals" },
};
// `wording` ({ button, title }) replaces the words, e.g. "Move responded" while a
// survey box is picked.
export default function PftMoveAll({ cards, onDone, scope, note, wording: wordingOverride }) {
  const wording = wordingOverride || WORDING[scope] || { button: "Move all", title: "Move all to Lung Center Referrals" };
  const [step, setStep] = useState("idle"); // idle | checking | ready | moving | review | done | error
  const [plan, setPlan] = useState(null); // { clear, withMatch, candidates }
  const [progress, setProgress] = useState({ done: 0, skipped: 0, failed: 0, merged: 0, added: 0 });
  const [error, setError] = useState(null);
  const stopRef = useRef(false);
  // Review of the cards that may already be in the system, one at a time.
  const [queue, setQueue] = useState([]);
  const [at, setAt] = useState(0);
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null); // matchKey
  const [diff, setDiff] = useState(null); // { target, plan, signature } when fields differ
  const [choices, setChoices] = useState(NO_CHOICES);
  const [busy, setBusy] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const newCards = cards.filter((c) => c.status === "new");

  const check = async () => {
    setError(null);
    setStep("checking");
    try {
      const candidates = await loadMatchCandidates();
      const clear = [];
      const withMatch = [];
      for (const c of newCards) (findCardMatches(c, candidates).length ? withMatch : clear).push(c);
      setPlan({ clear, withMatch, candidates });
      setStep("ready");
    } catch (e) {
      setError(e.message);
      setStep("error");
    }
  };

  const openCard = (list, i) => {
    setAt(i);
    setDiff(null);
    setChoices(NO_CHOICES);
    setReviewError(null);
    if (i >= list.length) {
      setStep("done");
      return;
    }
    const found = findCardMatches(list[i], plan.candidates);
    setMatches(found);
    setPicked(found.length === 1 ? matchKey(found[0]) : null);
    setStep("review");
  };

  const run = async () => {
    stopRef.current = false;
    setStep("moving");
    const p = { done: 0, skipped: 0, failed: 0, merged: 0, added: 0 };
    setProgress(p);
    const lateMatches = [];
    for (const card of plan.clear) {
      if (stopRef.current) break;
      try {
        await createPatientFromCard(card, { uncheckedItems: unchecked(card) }, plan.candidates);
        p.done += 1;
      } catch (e) {
        if (e instanceof MatchFoundError) lateMatches.push(card); // became a possible match (e.g. same person twice)
        else p.failed += 1;
      }
      setProgress({ ...p });
    }
    const list = [...plan.withMatch, ...lateMatches];
    setQueue(list);
    if (stopRef.current || !list.length) {
      p.skipped += stopRef.current ? list.length : 0;
      setProgress({ ...p });
      setStep("done");
      return;
    }
    openCard(list, 0);
  };

  const card = queue[at];
  const count = (key) => setProgress((p) => ({ ...p, [key]: p[key] + 1 }));
  const next = () => openCard(queue, at + 1);

  const merge = async () => {
    const target = diff ? diff.target : matches.find((m) => matchKey(m) === picked);
    if (!target) return;
    setBusy(true);
    setReviewError(null);
    try {
      let signature = diff && diff.signature;
      if (!diff) {
        // A fresh read. Anything that differs is asked about before merging.
        const loaded = await loadMergePlan(card, target);
        if (loaded.plan.conflicts > 0) {
          setDiff({ target, plan: loaded.plan, signature: loaded.signature });
          setBusy(false);
          return;
        }
        signature = loaded.signature;
      }
      await mergeIntoFirestorePatient(card, target, diff ? choices : NO_CHOICES, signature, { uncheckedItems: unchecked(card) });
      count("merged");
      setBusy(false);
      next();
    } catch (e) {
      setBusy(false);
      if (e instanceof PatientChangedError) {
        setDiff(null);
        setChoices(NO_CHOICES);
        setReviewError("This patient changed. Check again.");
      } else {
        setReviewError(e.message);
      }
    }
  };

  const addNew = async () => {
    setBusy(true);
    setReviewError(null);
    try {
      await createPatientFromCard(card, { allowMatches: true, uncheckedItems: unchecked(card) }, plan.candidates);
      count("added");
      setBusy(false);
      next();
    } catch (e) {
      setBusy(false);
      setReviewError(e.message);
    }
  };

  const skip = () => {
    count("skipped");
    next();
  };

  const stopReview = () => {
    setProgress((p) => ({ ...p, skipped: p.skipped + (queue.length - at) }));
    setStep("done");
  };

  const close = () => {
    if (step === "done") {
      const extra = [
        progress.merged ? `Merged ${progress.merged}.` : "",
        progress.added ? `Added ${progress.added} as new.` : "",
      ].filter(Boolean);
      onDone([`Moved ${progress.done} patient(s) into Lung Center Referrals.`, ...extra].join(" "));
    }
    setStep("idle");
    setPlan(null);
    setQueue([]);
  };

  const withUnchecked = plan ? plan.clear.filter((c) => unchecked(c) > 0).length : 0;
  const pickedMatch = matches.find((m) => matchKey(m) === picked);
  const newest = card && card.tests && card.tests[0];
  const cardUnchecked = card ? unchecked(card) : 0;

  return (
    <>
      <button onClick={check} disabled={!newCards.length || step !== "idle"} className={btn}>
        <Users className="w-3.5 h-3.5" />
        {wording.button}
      </button>
      {step !== "idle" && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Move all">
          <div className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl pft-modal-panel text-slate-100 p-5 space-y-4">
            <div className="flex items-center gap-2">
              {step === "review" ? <GitMerge className="w-5 h-5" /> : <Users className="w-5 h-5" />}
              <h3 className="text-base font-semibold">
                {step === "review" ? `Already in RegX? · ${at + 1} of ${queue.length}` : wording.title}
              </h3>
              <button
                onClick={close}
                disabled={step === "checking" || step === "moving" || step === "review"}
                aria-label="Close"
                className={`ml-auto ${btn}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {step === "checking" && (
              <p className="text-[13px] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking {newCards.length} patient(s) against the system…
              </p>
            )}

            {step === "ready" && (
              <div className="space-y-3 text-[13px]">
                {note && <p className="text-slate-300">{note}</p>}
                {plan.clear.length > 0 && (
                  <p>
                    <span className="font-semibold">{plan.clear.length}</span> patient(s) aren't in the system yet and will be added to Lung Center Referrals.
                    {withUnchecked > 0 && (
                      <span className="text-amber-200"> {withUnchecked} of them still have items that haven't been checked against the PDF.</span>
                    )}
                  </p>
                )}
                {plan.withMatch.length > 0 && (
                  <p className="text-amber-200">
                    <span className="font-semibold">{plan.withMatch.length}</span> patient(s) may already be in RegX. You'll check each one{plan.clear.length ? " next" : ""}.
                  </p>
                )}
                <div className="flex gap-2">
                  {plan.clear.length > 0 ? (
                    <button onClick={run} className={primary}>
                      Move {plan.clear.length} patient(s)
                    </button>
                  ) : (
                    plan.withMatch.length > 0 && (
                      <button onClick={run} className={primary}>
                        Check {plan.withMatch.length} patient(s)
                      </button>
                    )
                  )}
                  <button onClick={close} className={btn}>Cancel</button>
                </div>
              </div>
            )}

            {step === "moving" && (
              <div className="space-y-2 text-[13px]">
                <div className="h-2.5 rounded-full border border-white/15 bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.round(((progress.done + progress.failed) / (plan.clear.length || 1)) * 100)}%` }}
                  />
                </div>
                <p className="tabular-nums">
                  Moving… {progress.done} moved
                  {progress.failed ? `, ${progress.failed} couldn't be moved (try them one by one)` : ""}
                  {" "}of {plan.clear.length}.
                </p>
                <p className="text-[12px] text-amber-200">Keep this page open until it finishes.</p>
                <button onClick={() => { stopRef.current = true; }} className={btn}>
                  <Square className="w-3.5 h-3.5" />
                  Stop after this one
                </button>
              </div>
            )}

            {step === "review" && card && (
              <div className="space-y-3 text-[13px]">
                <p className="text-[12px] text-slate-300">
                  From PFT: <span className="font-semibold text-slate-100">{card.name || "(no name)"}</span> · DOB {card.dob || "—"} · MRN{" "}
                  {card.mrn9 || card.mrnRaw || "—"}
                  {newest && newest.date ? ` · PFT ${newest.date}` : ""}
                  {cardUnchecked > 0 && <span className="block text-amber-200">{cardUnchecked} item(s) not checked against the PDF</span>}
                </p>

                {diff ? (
                  <>
                    <p className="font-medium">These differ. Keep or use the PFT's?</p>
                    <PftMergeRows
                      plan={diff.plan}
                      choices={choices}
                      setChoice={(s, k, f, v) => setChoices((c) => withChoice(c, s, k, f, v))}
                      onlyDifferences
                      regxHref={patientHref(diff.target)}
                      pdfs={pdfsOfCard(card)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button onClick={merge} disabled={busy} className={primary}>
                        <GitMerge className="w-4 h-4" />
                        Merge
                      </button>
                      <button onClick={() => { setDiff(null); setChoices(NO_CHOICES); }} disabled={busy} className={btn}>Back</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-medium">{matches.length === 1 ? "Merge with:" : `${matches.length} possible matches. Pick one:`}</p>
                    {matches.length === 1 ? (
                      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                        <PftMatchCard match={matches[0]} />
                      </div>
                    ) : (
                      <div className="space-y-2" role="radiogroup" aria-label="Possible matches">
                        {matches.map((m) => (
                          <label
                            key={matchKey(m)}
                            className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${
                              picked === matchKey(m) ? "border-purple-300/40 bg-purple-500/15" : "border-white/10 bg-white/[0.03]"
                            }`}
                          >
                            <input
                              type="radio"
                              name="pft-review-pick"
                              checked={picked === matchKey(m)}
                              onChange={() => setPicked(matchKey(m))}
                              className="mt-1 accent-purple-500"
                              aria-label={`Pick ${m.record.name || m.record.fullName || "this record"}`}
                            />
                            <PftMatchCard match={m} />
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={merge} disabled={busy || !pickedMatch} className={primary}>
                        <GitMerge className="w-4 h-4" />
                        Merge
                      </button>
                      <button onClick={addNew} disabled={busy} className={btn}>Not this patient: add new</button>
                      <button onClick={skip} disabled={busy} className={btn}>Skip</button>
                      <button onClick={stopReview} disabled={busy} className={`ml-auto ${btn}`}>
                        <Square className="w-3.5 h-3.5" />
                        Stop
                      </button>
                    </div>
                  </>
                )}
                {busy && (
                  <p className="text-[12px] flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving…
                  </p>
                )}
                {reviewError && <p className="text-[12px] text-amber-200">{reviewError}</p>}
              </div>
            )}

            {step === "done" && (
              <div className="space-y-2 text-[13px]">
                <p className="tabular-nums">
                  Done. {progress.done} moved
                  {progress.merged ? `, ${progress.merged} merged` : ""}
                  {progress.added ? `, ${progress.added} added as new` : ""}
                  {progress.skipped ? `, ${progress.skipped} left here` : ""}
                  {progress.failed ? `, ${progress.failed} couldn't be moved (try them one by one)` : ""}.
                </p>
                <button onClick={close} className={btn}>Close</button>
              </div>
            )}

            {step === "error" && (
              <div className="space-y-2">
                <p className="text-[13px] text-amber-200">{error}</p>
                <button onClick={close} className={btn}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
